import { createClient, type SupabaseClient } from "@supabase/supabase-js";

type AuthConfig = Readonly<{ provider: "supabase"; url: string; publishableKey: string }>;

export type PlatformMfaPrompt = Readonly<
  | {
    kind: "enroll";
    factorId: string;
    qrCode: string;
    secret: string;
  }
  | {
    kind: "challenge";
    factorId: string;
    factorName: string;
  }
>;

export class PlatformAuthError extends Error {
  readonly restartAtEmail: boolean;

  constructor(message: string, restartAtEmail = false) {
    super(message);
    this.name = "PlatformAuthError";
    this.restartAtEmail = restartAtEmail;
  }
}

let clientPromise: Promise<SupabaseClient> | undefined;
const discardedClients = new WeakSet<SupabaseClient>();

const client = async (): Promise<SupabaseClient> => {
  clientPromise ??= (async () => {
    const response = await fetch("/api/v1/platform/auth/config", {
      credentials: "same-origin",
      headers: { accept: "application/json" }
    });
    if (!response.ok) throw new PlatformAuthError("Platform sign-in is temporarily unavailable.");
    const config = await response.json() as AuthConfig;
    return createClient(config.url, config.publishableKey, {
      auth: {
        autoRefreshToken: false,
        detectSessionInUrl: false,
        persistSession: false
      }
    });
  })();
  try {
    return await clientPromise;
  } catch (reason) {
    clientPromise = undefined;
    if (reason instanceof PlatformAuthError) throw reason;
    throw new PlatformAuthError("Platform sign-in is temporarily unavailable.");
  }
};

const discardProviderSession = async (auth: SupabaseClient): Promise<void> => {
  if (discardedClients.has(auth)) {
    clientPromise = undefined;
    return;
  }
  discardedClients.add(auth);
  try {
    await auth.auth.signOut({ scope: "local" });
  } catch {
    // No session is persisted and the client reference is discarded below. A
    // provider sign-out transport error must not obscure a successfully issued
    // Phenera cookie or leave the UI offering a duplicate submission.
  } finally {
    // The client never persists its session. Dropping this final reference also
    // prevents a completed or abandoned factor flow from being reused in memory.
    clientPromise = undefined;
  }
};

const establishPlatformSession = async (accessToken: string): Promise<void> => {
  const response = await fetch("/api/v1/platform/auth/session", {
    method: "POST",
    credentials: "same-origin",
    headers: { authorization: `Bearer ${accessToken}` }
  });
  if (response.status === 403) {
    throw new PlatformAuthError("This identity is not approved as a Phenera platform owner.", true);
  }
  if (!response.ok) {
    throw new PlatformAuthError("Your identity was verified, but a safe platform session could not be established. Start sign-in again.", true);
  }
};

const assertFreshAal2 = async (auth: SupabaseClient, accessToken: string): Promise<void> => {
  let assurance: Awaited<ReturnType<typeof auth.auth.mfa.getAuthenticatorAssuranceLevel>>;
  try {
    assurance = await auth.auth.mfa.getAuthenticatorAssuranceLevel(accessToken);
  } catch {
    throw new PlatformAuthError("Authenticator verification succeeded, but its fresh security level could not be confirmed. Start sign-in again.", true);
  }
  if (assurance.error !== null || assurance.data.currentLevel !== "aal2") {
    throw new PlatformAuthError("Authenticator verification did not produce the required AAL2 session. Start sign-in again.", true);
  }
};

const completeAlreadyAal2Session = async (auth: SupabaseClient, accessToken: string): Promise<void> => {
  try {
    await assertFreshAal2(auth, accessToken);
    await establishPlatformSession(accessToken);
  } finally {
    await discardProviderSession(auth);
  }
};

const preparePlatformMfa = async (auth: SupabaseClient, accessToken: string): Promise<PlatformMfaPrompt | undefined> => {
  let assurance: Awaited<ReturnType<typeof auth.auth.mfa.getAuthenticatorAssuranceLevel>>;
  try {
    assurance = await auth.auth.mfa.getAuthenticatorAssuranceLevel();
  } catch {
    throw new PlatformAuthError("Your email was verified, but the authenticator security level could not be checked. Start sign-in again.", true);
  }
  if (assurance.error !== null || assurance.data.currentLevel === null) {
    throw new PlatformAuthError("Your email was verified, but the authenticator security level could not be checked. Start sign-in again.", true);
  }
  if (assurance.data.currentLevel === "aal2") {
    await completeAlreadyAal2Session(auth, accessToken);
    return undefined;
  }
  if (assurance.data.currentLevel !== "aal1") {
    throw new PlatformAuthError("Supabase returned an unsupported authenticator security level. Start sign-in again.", true);
  }

  let factors: Awaited<ReturnType<typeof auth.auth.mfa.listFactors>>;
  try {
    factors = await auth.auth.mfa.listFactors();
  } catch {
    throw new PlatformAuthError("Your email was verified, but registered authenticators could not be checked. Start sign-in again.", true);
  }
  if (factors.error !== null) {
    throw new PlatformAuthError("Your email was verified, but registered authenticators could not be checked. Start sign-in again.", true);
  }
  const verifiedTotp = factors.data.totp[0];
  if (verifiedTotp !== undefined) {
    return {
      kind: "challenge",
      factorId: verifiedTotp.id,
      factorName: verifiedTotp.friendly_name?.trim() || "Authenticator app"
    };
  }

  let enrollment: Awaited<ReturnType<typeof auth.auth.mfa.enroll>>;
  try {
    enrollment = await auth.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: "Phenera Platform Owner",
      issuer: "Phenera"
    });
  } catch {
    throw new PlatformAuthError("Your email was verified, but authenticator setup could not be started. Start sign-in again.", true);
  }
  if (enrollment.error !== null || enrollment.data.type !== "totp") {
    throw new PlatformAuthError("Your email was verified, but authenticator setup could not be started. Start sign-in again.", true);
  }
  return {
    kind: "enroll",
    factorId: enrollment.data.id,
    qrCode: enrollment.data.totp.qr_code,
    secret: enrollment.data.totp.secret
  };
};

export const requestPlatformCode = async (email: string): Promise<void> => {
  const auth = await client();
  let result: Awaited<ReturnType<typeof auth.auth.signInWithOtp>>;
  try {
    result = await auth.auth.signInWithOtp({ email, options: { shouldCreateUser: false } });
  } catch {
    throw new PlatformAuthError("A platform sign-in code could not be sent.");
  }
  if (result.error !== null) throw new PlatformAuthError("A platform sign-in code could not be sent.");
};

export const verifyPlatformCode = async (email: string, token: string): Promise<PlatformMfaPrompt | undefined> => {
  const auth = await client();
  let result: Awaited<ReturnType<typeof auth.auth.verifyOtp>>;
  try {
    result = await auth.auth.verifyOtp({ email, token, type: "email" });
  } catch {
    throw new PlatformAuthError("That email sign-in code is invalid or expired.");
  }
  if (result.error !== null || result.data.session?.access_token === undefined) {
    throw new PlatformAuthError("That email sign-in code is invalid or expired.");
  }
  try {
    return await preparePlatformMfa(auth, result.data.session.access_token);
  } catch (reason) {
    if (reason instanceof PlatformAuthError && reason.restartAtEmail) await discardProviderSession(auth);
    throw reason;
  }
};

export const verifyPlatformAuthenticator = async (factorId: string, code: string): Promise<void> => {
  if (!/^\d{6}$/u.test(code)) throw new PlatformAuthError("Enter the six-digit code from your authenticator app.");
  const auth = await client();
  let challenge: Awaited<ReturnType<typeof auth.auth.mfa.challenge>>;
  try {
    challenge = await auth.auth.mfa.challenge({ factorId });
  } catch {
    throw new PlatformAuthError("The authenticator challenge could not be started. Wait for a new code and try again.");
  }
  if (challenge.error !== null) {
    throw new PlatformAuthError("The authenticator challenge could not be started. Wait for a new code and try again.");
  }

  let verification: Awaited<ReturnType<typeof auth.auth.mfa.verify>>;
  try {
    verification = await auth.auth.mfa.verify({ factorId, challengeId: challenge.data.id, code });
  } catch {
    throw new PlatformAuthError("That authenticator code is invalid or expired. Wait for the next code and try again.");
  }
  if (verification.error !== null) {
    throw new PlatformAuthError("That authenticator code is invalid or expired. Wait for the next code and try again.");
  }
  try {
    await assertFreshAal2(auth, verification.data.access_token);
    await establishPlatformSession(verification.data.access_token);
  } finally {
    await discardProviderSession(auth);
  }
};

export const cancelPlatformMfaEnrollment = async (factorId: string): Promise<void> => {
  const auth = await client();
  let result: Awaited<ReturnType<typeof auth.auth.mfa.unenroll>>;
  try {
    result = await auth.auth.mfa.unenroll({ factorId });
  } catch {
    throw new PlatformAuthError("Authenticator setup could not be cancelled safely. Try cancel again before leaving this screen.");
  }
  if (result.error !== null) {
    throw new PlatformAuthError("Authenticator setup could not be cancelled safely. Try cancel again before leaving this screen.");
  }
  await discardProviderSession(auth);
};

export const abandonPlatformMfa = async (): Promise<void> => {
  const auth = await client();
  await discardProviderSession(auth);
};

export const signOutPlatform = async (): Promise<void> => {
  await fetch("/api/v1/platform/auth/logout", { method: "POST", credentials: "same-origin" });
};
